<div class="fb-table" [class.selectable]="selectable()">
  <!-- Panel header: title + Export / New Feedback actions. -->
  <div class="fb-panel-head">
    <span class="fb-panel-title">{{ heading() }}</span>
    <div class="fb-panel-actions">
      @if (count()) {
        <span class="fb-panel-count">{{ count() }} {{ count() === 1 ? 'item' : 'items' }}</span>
      }
      <button type="button" class="fb-action fb-action-ghost" (click)="exportFeedback.emit()">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
          stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        Export
      </button>
      <button type="button" class="fb-action fb-action-primary" (click)="newFeedback.emit()">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"
          stroke-linecap="round" stroke-linejoin="round">
          <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
        </svg>
        New Feedback
      </button>
    </div>
  </div>

  <!-- Filters menu and the applied filters as chips; sort on the right. -->
  <div class="fb-filters">
    <fb-filter-menu [fields]="filterFields()" [values]="filterValues()" (valuesChange)="onFiltersApplied($event)" />

    <div class="fb-sort">
      <span class="fb-sort-label">Sort</span>
      @for (chip of sortChips(); track chip.col; let i = $index) {
        <span class="fb-sort-chip">
          @if (isMultiSort()) {
            <span class="fb-sort-num">{{ i + 1 }}</span>
          }
          <span>{{ chip.label }}</span>
          <button
            type="button"
            class="fb-sort-btn"
            (click)="toggleChipDirection(chip.col)"
            [attr.aria-label]="chip.dir === 'asc' ? 'Ascending — flip' : 'Descending — flip'"
          >
            <svg class="fb-sort-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round">
              @if (chip.dir === 'asc') {
                <polyline points="18 15 12 9 6 15" />
              } @else {
                <polyline points="6 9 12 15 18 9" />
              }
            </svg>
          </button>
          @if (isMultiSort()) {
            <button type="button" class="fb-sort-btn" (click)="removeSort(chip.col)" aria-label="Remove sort">
              <svg class="fb-sort-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          }
        </span>
      }
      @if (addableColumns().length) {
        <select
          #addSelect
          class="fb-sort-add"
          aria-label="Add sort column"
          (change)="addSort(addSelect.value); addSelect.value = ''"
        >
          <option value="">+ Add sort</option>
          @for (column of addableColumns(); track column.key) {
            <option [value]="column.key">{{ column.label }}</option>
          }
        </select>
      }
    </div>

    @if (!isDefaultSort()) {
      <button type="button" class="fb-reset" (click)="resetSort()">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
          stroke-linecap="round" stroke-linejoin="round">
          <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" /><line x1="3" y1="3" x2="21" y2="21" />
        </svg>
        Reset
      </button>
    }
  </div>

  <!-- Bulk bar: one status for every selected row in view. -->
  @if (selectable() && selectedRows().length) {
    <div class="fb-bulk-bar" role="region" aria-label="Bulk actions">
      <span class="fb-bulk-count">{{ selectedRows().length }} selected</span>
      <select
        #bulkStatusSelect
        class="fb-select"
        aria-label="Set status for selected feedback"
        (change)="onBulkStatus(bulkStatusSelect.value); bulkStatusSelect.value = ''"
      >
        <option value="">Set status…</option>
        @for (option of bulkStatusOptions(); track option.value) {
          <option [value]="option.value">{{ option.label }}</option>
        }
      </select>
      <button type="button" class="fb-bulk-clear" (click)="clearSelection()">Clear selection</button>
    </div>
  }

  <div class="fb-head">
    @if (selectable()) {
      <div class="fb-th center fb-check-cell">
        <input
          #selectAllCheckbox
          type="checkbox"
          class="fb-check"
          aria-label="Select all feedback in view"
          [checked]="allRowsSelected()"
          [indeterminate]="someRowsSelected()"
          [disabled]="!items().length"
          (change)="toggleAllRows(selectAllCheckbox.checked)"
        />
      </div>
    }
    @for (column of columns; track column.key) {
      @let entry = sortEntry(column.key);
      <div
        class="fb-th"
        [class.center]="column.center"
        [class.sortable]="column.sortable"
        [class.sorted]="entry"
        [attr.title]="column.sortable ? 'Click to sort · Shift+Click to add as secondary sort' : null"
        (click)="column.sortable && toggleSort(column.key, $event.shiftKey)"
      >
        <span class="fb-th-label">{{ column.label }}</span>
        @if (entry) {
          <span class="fb-th-pill">
            @if (isMultiSort()) {
              <span>{{ entry.index + 1 }}</span>
            }
            <svg class="fb-th-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              @if (entry.dir === 'asc') {
                <polyline points="18 15 12 9 6 15" />
              } @else {
                <polyline points="6 9 12 15 18 9" />
              }
            </svg>
          </span>
        }
      </div>
    }
  </div>

  @if (loading()) {
    <fb-loading-bar class="fb-loading" [height]="3" [rounded]="false" />
  }

  @for (row of items(); track row.id) {
    <div class="fb-row" [class.selected]="selectable() && isRowSelected(row.id)" [class.stale]="loading()">
      @if (selectable()) {
        <div class="fb-td center fb-check-cell">
          <input
            #rowCheckbox
            type="checkbox"
            class="fb-check"
            [attr.aria-label]="'Select ' + (row.element || 'feedback')"
            [checked]="isRowSelected(row.id)"
            (change)="toggleRowSelection(row.id, rowCheckbox.checked)"
          />
        </div>
      }
      <div class="fb-td">
        <span class="cat-pill">{{ humanize(row.category) }}</span>
      </div>
      <div class="fb-td text element" [title]="row.element">
        <span class="element-text">{{ row.element }}</span>
        @if (row.unreadComments) {
          <span
            class="unread-badge"
            [title]="
              row.unreadComments === 1
                ? '1 unread comment'
                : row.unreadComments + ' unread comments'
            "
            >{{ row.unreadComments }}</span
          >
        }
      </div>
      <div class="fb-td url" [title]="row.url">
        @if (isWebUrl(row.url)) {
          <a
            class="url-open"
            [href]="pageLink()(row.url)"
            target="_blank"
            rel="noopener noreferrer"
            [attr.aria-label]="'Open ' + urlPath(row.url) + ' in a new window'"
            title="Open page in a new window"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
            </svg>
          </a>
        }
        <span class="url-text">{{ urlPath(row.url) }}</span>
      </div>
      <div class="fb-td center">
        <span class="score" [style.color]="scoreColor(row.score)">{{ scoreLabel(row.score) }}</span>
      </div>
      <div class="fb-td center">
        <span class="screen-ic" [style.color]="screenColor(row.screen)" [title]="row.screen">
          @switch (row.screen) {
            @case ('mobile') {
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round">
                <rect x="5" y="2" width="14" height="20" rx="2" /><line x1="12" y1="18" x2="12.01" y2="18" />
              </svg>
            }
            @case ('tablet') {
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round">
                <rect x="4" y="2" width="16" height="20" rx="2" /><line x1="12" y1="18" x2="12.01" y2="18" />
              </svg>
            }
            @default {
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" />
                <line x1="12" y1="17" x2="12" y2="21" />
              </svg>
            }
          }
        </span>
      </div>
      <div class="fb-td">
        <fb-status-chip
          [statuses]="statuses()"
          [current]="row.status"
          (statusChange)="statusChange.emit({ row, status: $event })" />
      </div>
      <div class="fb-td center">
        @let type = typeStyle(row.type);
        <span class="type-pill" [style.background]="type.bg" [style.color]="type.text">{{ type.label }}</span>
      </div>
      <div class="fb-td created">{{ row.created | date: 'yyyy-MM-dd' }}</div>
      <div class="fb-td center">
        <button class="open-btn" type="button" (click)="openFeedback.emit(row)" aria-label="Open feedback">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
            <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
          </svg>
        </button>
      </div>
    </div>
  } @empty {
    @if (loading()) {
      <!-- Placeholder rows while the first page loads, so the panel isn't blank. -->
      @for (placeholder of placeholderRows; track placeholder) {
        <div class="fb-row fb-row-placeholder" aria-hidden="true">
          @if (selectable()) {
            <div class="fb-td"></div>
          }
          @for (column of columns; track column.key) {
            <div class="fb-td">
              @if (column.label) {
                <span class="fb-placeholder-bar"></span>
              }
            </div>
          }
        </div>
      }
      <span class="fb-sr-only" role="status">Loading feedback…</span>
    } @else {
      <div class="fb-empty">No feedback yet</div>
    }
  }

  @if (count() > perPage() && perPage() > 0) {
    <div class="fb-foot">
      <fb-pagination
        [page]="page()"
        [total]="count()"
        [perPage]="perPage()"
        label="feedback"
        (pageChange)="pageChange.emit($event)"
      />
    </div>
  }
</div>
